import Vue from "vue";
import { ActionTree, MutationTree, GetterTree } from "vuex";

const initialState: ProfileState = {
  all: [],
  name: "",
};

const getters: GetterTree<ProfileState, RootState> = {
  all(state: ProfileState): Profile[] {
    return state.all;
  },
  name(state: ProfileState): string {
    return state.name;
  },
};

const mutations: MutationTree<ProfileState> = {
  RESET(state: ProfileState) {
    state.all = [];
  },

  SET_MODELS(state: ProfileState, payload: Profile[]) {
    state.all = payload.sort((profile_new: Profile, profile_old: Profile) => profile_new.slot - profile_old.slot);
  },

  UPDATE_MODEL(state: ProfileState, payload: Profile) {
    const index = state.all.findIndex((model: Profile) => model.slot === payload.slot);

    if (index === -1) {
      state.all.push(payload);
    } else {
      Vue.set(state.all, index, payload);
    }
  },

  SET_NAME(state: ProfileState, payload: string) {
    state.name = payload;
  },
};

const actions: ActionTree<ProfileState, RootState> = {
  reset({ commit }) {
    commit("RESET");
  },

  name({ commit, rootState, state }, payload?: { skipStore: boolean }) {
    if (state.name && (!payload || !payload.skipStore)) {
      return new Promise((resolve) => resolve(this.getters["profile/name"]));
    }

    return rootState.api
      .get(`config/name`)
      .then((response: { data: string }) => {
        commit("SET_NAME", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  index({ commit, rootState, state }) {
    if (state.all.length > 0) {
      return new Promise((resolve) => resolve(this.getters["Profile/all"]));
    }

    return rootState.api
      .get(`config/profiles`)
      .then((response: { data: Profile[] }) => {
        commit("SET_MODELS", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  read({ commit, rootState, state }, slot: number) {
    return rootState.api
      .get(`config/profiles/${slot}`)
      .then((response: { data: Profile }) => {
        commit("UPDATE_MODEL", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  download({ commit, rootState, state }, payload: { slot: number; name: string }) {
    var xhr = new XMLHttpRequest();
    xhr.open("GET", `${process.env.VUE_APP_API_URL}/config/profiles/${payload.slot}/download`);
    xhr.responseType = "arraybuffer";

    xhr.onload = function () {
      if (xhr.status === 200) {
        var blob = new Blob([xhr.response], { type: "application/octet-stream" });
        var objectUrl = URL.createObjectURL(blob);
        var fileLink = document.createElement("a");
        document.body.appendChild(fileLink);
        fileLink.href = objectUrl;
        fileLink.download = "Profile - " + payload.name;
        fileLink.click();
      }
    };
    xhr.send();
  },

  commit({ commit, rootState, state }, payload: { slot: number; name: string }) {
    return rootState.api
      .put(`config/profiles/${payload.slot}/save`, { name: payload.name })
      .then((response: { data: {} }) => {
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  lock({ commit, rootState, state }, payload: { slot: number; protected: boolean }) {
    const config = { headers: { "Content-Type": "application/json" } };
    return rootState.api
      .put(`config/profiles/${payload.slot}/protected`, payload.protected, config)
      .then((response: { data: {} }) => {
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  recall({ commit, rootState, state }, payload: { slot: number; data: any }) {
    return rootState.api
      .put(`config/profiles/${payload.slot}/recall`, payload.data)
      .then((response: { data: {} }) => {
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  async upload({ commit, rootState, state }, payload: { slot: number; data: any }) {
    const options = { method: "put", body: payload.data };

    try {
      const response = await fetch(`${process.env.VUE_APP_API_URL}/config/profiles/${payload.slot}/upload`, options);
      if (!response.ok) {
        const data = await response.json();
        return Promise.reject(data);
      }
      return response;
    } catch (e) {
      throw new Error(e);
    }
  },

  delete({ commit, rootState, state }, slot) {
    return rootState.api
      .put(`config/profiles/${slot}/clear`, {})
      .then((response: { data: {} }) => {
        return this.dispatch("profile/read", slot).then((response: { data: {} }) => {
          Promise.resolve(response.data);
        });
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  exists({ commit, rootState, state }, slot) {
    return rootState.api
      .get(`config/profiles/${slot}/name`, {})
      .then((response: { data: string }) => {
        return Promise.resolve(response.data !== "__empty__");
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
